import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Copy, Download, Globe2, Link2, Trash2, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';

export default function AdminEmbed() {
    const [settings, setSettings] = useState({});
    const [status, setStatus] = useState(null);
    const [busy, setBusy] = useState(false);
    const [shortLinks, setShortLinks] = useState([]);
    const [newLink, setNewLink] = useState({ path: '/', kind: 'generic', campaign: '' });

    const load = async () => {
        const [s, cfg, sl] = await Promise.all([
            api.get('/admin/settings'),
            api.get('/public/share-config'),
            api.get('/short-links').catch(() => ({ data: [] })),
        ]);
        setSettings(s.data || {});
        setStatus(cfg.data);
        setShortLinks(sl.data || []);
    };
    useEffect(() => { load(); }, []);

    const save = async () => {
        setBusy(true);
        try {
            await api.put('/admin/settings', {
                public_share_url: settings.public_share_url || null,
                embed_mode: settings.embed_mode || 'none',
                embed_allowed_origins: settings.embed_allowed_origins || null,
            });
            toast.success('Configuración guardada');
            load();
        } catch (e) { toast.error('Error al guardar'); }
        finally { setBusy(false); }
    };

    const createLink = async () => {
        if (!newLink.path.startsWith('/')) { toast.error('El path debe comenzar con /'); return; }
        try {
            await api.post('/short-links', { path: newLink.path, kind: newLink.kind, campaign: newLink.campaign || null });
            toast.success('Short link creado');
            setNewLink({ path: '/', kind: 'generic', campaign: '' });
            load();
        } catch (e) { toast.error('Error'); }
    };

    const deleteLink = async (code) => {
        if (!window.confirm('¿Eliminar este short link?')) return;
        await api.delete(`/short-links/${code}`);
        load();
    };

    const shortDomain = (status?.public_share_url ? new URL(status.public_share_url).origin : window.location.origin);
    const shortPrefix = status?.public_share_url ? '/s' : '/api/s'; // parent should proxy /s → /api/s

    const marketplaceOrigin = window.location.origin;
    const parentOrigin = status?.parent_origin || 'https://tu-dominio.com';

    const embedSnippet = `<!-- OK TE LO VENDO — Embed en dominio principal -->
<!DOCTYPE html>
<html lang="es"><head>
<meta charset="utf-8" />
<title>Ok te lo vendo</title>
<style>html,body,iframe{margin:0;padding:0;border:0;width:100%;height:100vh;overflow:hidden}</style>
</head><body>
<iframe id="okv-iframe" src="${marketplaceOrigin}" allow="clipboard-write; payment" referrerpolicy="no-referrer-when-downgrade"></iframe>
<script>
(function(){
  var iframe = document.getElementById('okv-iframe');
  var marketplaceOrigin = '${marketplaceOrigin}';
  var parentBase = window.location.pathname.replace(/\/[^/]*$/, ''); // parent mount path e.g. /tienda

  // Extract intended internal path from current parent URL (after parentBase)
  function currentEmbedPath(){
    var p = window.location.pathname;
    if (parentBase && p.indexOf(parentBase) === 0) p = p.slice(parentBase.length) || '/';
    return p + window.location.search + window.location.hash;
  }

  // On load, send intended path to iframe via ?embed_path=...
  var initial = currentEmbedPath();
  if (initial && initial !== '/') {
    var u = new URL(iframe.src);
    u.searchParams.set('embed_path', initial);
    iframe.src = u.toString();
  }

  // Receive navigate events from iframe and mirror to parent URL
  window.addEventListener('message', function(evt){
    if (evt.origin !== marketplaceOrigin) return;
    var msg = evt.data;
    if (!msg || msg.type !== 'okv:navigate' || typeof msg.path !== 'string') return;
    var newUrl = parentBase + msg.path;
    if (newUrl !== (window.location.pathname + window.location.search + window.location.hash)) {
      history.replaceState({}, '', newUrl);
    }
  });

  // Handle browser back/forward: sync back to iframe
  window.addEventListener('popstate', function(){
    iframe.contentWindow && iframe.contentWindow.postMessage({ type: 'okv:navigate', path: currentEmbedPath() }, marketplaceOrigin);
  });
})();
</script>
</body></html>`;

    const copyEmbedCode = async () => {
        try { await navigator.clipboard.writeText(embedSnippet); toast.success('Snippet copiado'); } catch { toast.error('Error'); }
    };

    const downloadHtml = () => {
        const blob = new Blob([embedSnippet], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a'); a.href = url; a.download = 'okv-embed-host.html'; a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div className="space-y-4" data-testid="admin-embed-page">
            <div className="flex items-center gap-2"><Globe2 className="h-6 w-6 text-red-600" /><h1 className="text-2xl font-semibold">Embed & Dominio principal</h1></div>
            <p className="text-sm text-muted-foreground">Configura cómo se compartirán los enlaces de tu marketplace para que los usuarios siempre permanezcan en tu dominio principal.</p>

            {status && (
                <div className="flex flex-wrap items-center gap-2 text-sm">
                    Estado:{' '}
                    <Badge className={status.public_share_url ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}>
                        {status.public_share_url ? `Configurado → ${status.public_share_url}` : 'Sin configurar (usando dominio actual)'}
                    </Badge>
                    <Badge className="bg-slate-100 text-slate-700">Modo: {status.embed_mode || 'none'}</Badge>
                </div>
            )}

            <Card>
                <CardHeader><CardTitle>1. Dominio público para compartir</CardTitle></CardHeader>
                <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="md:col-span-2">
                        <Label>URL pública base (donde vive el marketplace embebido)</Label>
                        <Input
                            value={settings.public_share_url || ''}
                            onChange={e => setSettings({ ...settings, public_share_url: e.target.value })}
                            placeholder="https://okgo.mx/tienda"
                            data-testid="embed-public-url"
                        />
                        <p className="text-xs text-muted-foreground mt-1">Todos los links (QR, share, embed, deep-links, checkout return) usarán esta base. Ejemplo: si publicas <code>https://okgo.mx/tienda</code>, un link a un anuncio será <code>https://okgo.mx/tienda/a/&lt;id&gt;</code>. Si lo dejas vacío, se usa el dominio actual.</p>
                    </div>
                    <div>
                        <Label>Modo de integración</Label>
                        <Select value={settings.embed_mode || 'none'} onValueChange={v => setSettings({ ...settings, embed_mode: v })}>
                            <SelectTrigger data-testid="embed-mode"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="none">Ninguno (dominio directo)</SelectItem>
                                <SelectItem value="iframe">Iframe + postMessage (recomendado)</SelectItem>
                                <SelectItem value="proxy">Reverse proxy (Nginx / Cloudflare)</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label>Orígenes permitidos (opcional, CSV)</Label>
                        <Input
                            value={settings.embed_allowed_origins || ''}
                            onChange={e => setSettings({ ...settings, embed_allowed_origins: e.target.value })}
                            placeholder="https://okgo.mx, https://www.okgo.mx"
                            data-testid="embed-allowed-origins"
                        />
                    </div>
                    <div className="md:col-span-2 flex justify-end pt-2">
                        <Button onClick={save} disabled={busy} className="bg-red-600 hover:bg-red-700" data-testid="embed-save">Guardar</Button>
                    </div>
                </CardContent>
            </Card>

            <Card>
                <CardHeader><CardTitle>2. Código HTML para tu dominio principal</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                    <p className="text-sm text-muted-foreground">Pégate este HTML en <strong>{parentOrigin}</strong> (ruta configurada). Sincroniza la URL del navegador con el iframe para que los usuarios vean siempre tu dominio pero puedan compartir cualquier link interno.</p>
                    <Textarea readOnly value={embedSnippet} rows={14} className="font-mono text-[10px]" data-testid="embed-html-snippet" />
                    <div className="flex gap-2">
                        <Button variant="outline" onClick={copyEmbedCode} data-testid="embed-copy-snippet"><Copy className="h-4 w-4 mr-1" />Copiar HTML</Button>
                        <Button variant="outline" onClick={downloadHtml} data-testid="embed-download-html"><Download className="h-4 w-4 mr-1" />Descargar .html</Button>
                    </div>
                    <div className="text-xs text-muted-foreground border-l-2 border-red-300 pl-3 space-y-2">
                        <p><strong>Alternativa (reverse proxy):</strong> si controlas Nginx/Cloudflare del dominio principal, puedes proxear <code>{parentOrigin}/tienda/*</code> → <code>{marketplaceOrigin}/*</code> y elegir modo "proxy" arriba. En ese caso no necesitas el HTML de arriba.</p>
                        <p><strong>Short links bonitos:</strong> para que <code>{parentOrigin}/s/&lt;code&gt;</code> funcione, añade a tu Nginx / Cloudflare Worker una regla que proxee <code>/s/*</code> → <code>{marketplaceOrigin}/api/s/*</code> (301 o proxy pass). Si no, comparte directamente <code>{marketplaceOrigin}/api/s/&lt;code&gt;</code>.</p>
                    </div>
                </CardContent>
            </Card>

            <Card>
                <CardHeader><CardTitle className="flex items-center gap-2"><Link2 className="h-5 w-5 text-red-600" />3. Link shortener (opcional — SMS/WhatsApp/QR físicos)</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                        <div><Label className="text-xs">Path interno</Label><Input value={newLink.path} onChange={e => setNewLink({ ...newLink, path: e.target.value })} placeholder="/a/abc123" data-testid="shortlink-path" /></div>
                        <div><Label className="text-xs">Tipo</Label>
                            <Select value={newLink.kind} onValueChange={v => setNewLink({ ...newLink, kind: v })}>
                                <SelectTrigger data-testid="shortlink-kind"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="generic">Genérico</SelectItem>
                                    <SelectItem value="ad">Anuncio</SelectItem>
                                    <SelectItem value="user">Usuario</SelectItem>
                                    <SelectItem value="campaign">Campaña</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div><Label className="text-xs">Campaña (opcional)</Label><Input value={newLink.campaign} onChange={e => setNewLink({ ...newLink, campaign: e.target.value })} placeholder="whatsapp-julio" data-testid="shortlink-campaign" /></div>
                        <div className="flex items-end"><Button onClick={createLink} className="bg-red-600 hover:bg-red-700 w-full" data-testid="shortlink-create"><Plus className="h-4 w-4 mr-1" />Crear</Button></div>
                    </div>
                    <div className="space-y-1">
                        {shortLinks.length === 0 && <div className="text-sm text-muted-foreground py-4 text-center">Sin short links aún.</div>}
                        {shortLinks.map(sl => (
                            <div key={sl.code} className="flex flex-wrap items-center gap-2 border rounded-lg p-2">
                                <code className="text-xs font-mono bg-secondary px-2 py-1 rounded">{shortDomain}{shortPrefix}/{sl.code}</code>
                                <span className="text-xs text-muted-foreground">→ {sl.path}</span>
                                <Badge variant="outline" className="text-[10px]">{sl.kind}</Badge>
                                {sl.campaign && <Badge className="bg-slate-100 text-slate-700 text-[10px]">{sl.campaign}</Badge>}
                                <Badge className="bg-emerald-50 text-emerald-800 text-[10px] ml-auto">{sl.clicks} clicks</Badge>
                                <Button size="icon" variant="ghost" onClick={() => { navigator.clipboard.writeText(`${shortDomain}${shortPrefix}/${sl.code}`); toast.success('Copiado'); }}><Copy className="h-4 w-4" /></Button>
                                <Button size="icon" variant="ghost" onClick={() => deleteLink(sl.code)} data-testid={`shortlink-delete-${sl.code}`}><Trash2 className="h-4 w-4 text-red-600" /></Button>
                            </div>
                        ))}
                    </div>
                </CardContent>
            </Card>

            <div className="text-xs text-muted-foreground pt-4 border-t">
                <p>Rutas cortas ya disponibles nativamente: <code>/a/:id</code> → anuncio • <code>/u/:nickname</code> → perfil público</p>
            </div>
        </div>
    );
}
